import React, { useCallback, useEffect, useState } from "react";
import { useDisclosure } from "@chakra-ui/react";
import { BenchmarkType } from "build-ts/enums";
import { DatePeriodEnum } from "build-ts/relative_date";
import { BenchmarkEntityProto } from "build-ts/knowledege_graph";
import * as MetricService from "services/MetricService";
import showToast from "hooks/useCustomToast";
import { useConfigurationContext } from "context/ConfigurationContext";
import BenchmarkListComponent from "modules/benchmark/components/BenchmarkList";

interface BenchmarkListProps {
  splitDetailOpen: boolean;
}

const BenchmarkList: React.FC<BenchmarkListProps> = ({ splitDetailOpen }) => {
  const { setBenchmarkList, benchmarkList, handleEditBenchmark } = useConfigurationContext();
  const [isLoading, setIsLoading] = useState(true);
  const { isOpen: isCreateModalOpen, onOpen: onOpenCreateModal, onClose: onCloseCreateModal } = useDisclosure();

  const fetchAllBenchmarks = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await MetricService.getAllBenchmarks();
      if (data) setBenchmarkList(data);
    } catch {
      showToast("Error fetching benchmarks", "", "error");
    } finally {
      setIsLoading(false);
    }
  }, [setBenchmarkList]);

  useEffect(() => {
    if (benchmarkList.length === 0) {
      fetchAllBenchmarks();
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleCreateBenchmark = useCallback(
    async (name: string, type: BenchmarkType, granularity?: DatePeriodEnum) => {
      try {
        const newBenchmark = BenchmarkEntityProto.create({
          header: { name, id: "" },
          benchmark_type: type,
          versus_filters: [],
          definition_mapping: [],
          granularity: type === BenchmarkType.TARGET_BENCHMARK ? granularity || DatePeriodEnum.DAY : undefined,
        });
        const savedBenchmark = await MetricService.saveBenchmark(newBenchmark);
        if (savedBenchmark) {
          showToast("Benchmark created successfully", "", "success");
          await fetchAllBenchmarks();
          handleEditBenchmark(savedBenchmark.header!);
        }
      } catch {
        showToast("Error creating benchmark", "", "error");
      }
    },
    [fetchAllBenchmarks, handleEditBenchmark]
  );

  return (
    <BenchmarkListComponent
      splitDetailOpen={splitDetailOpen}
      isLoading={isLoading}
      isCreateModalOpen={isCreateModalOpen}
      onOpenCreateModal={onOpenCreateModal}
      onCloseCreateModal={onCloseCreateModal}
      onCreateBenchmark={handleCreateBenchmark}
    />
  );
};

export default BenchmarkList;
